import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { User as UserIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { getVisitsZoneSettings, listZoneVisits, type VisitSummaryDto } from '@/api/visits';
import { TotalScoreBadge } from '@/components/visits/scoreDisplay';

const DOT = 'bg-emerald-500';

function isoDayBounds(dayIso: string): { from: string; to: string } {
  const [y, m, d] = dayIso.split('-').map(Number);
  return {
    from: new Date(y, m - 1, d, 0, 0, 0).toISOString(),
    to: new Date(y, m - 1, d, 23, 59, 59).toISOString(),
  };
}

/**
 * Week-report visits panel — Alexey: "на вкладке отчёт недели та же самая
 * панель" as the day report. Lists that week's submitted visits with a
 * quick average-total stat; each row navigates to the visit's own full
 * page, same "expand" idiom as DayVisitsCard.
 */
export function WeekVisitsCard({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const navigate = useNavigate();

  const { data: settings } = useSWR(
    zoneId ? ['report:week-visits-settings', zoneId] : null,
    () => getVisitsZoneSettings(zoneId).then(r => r.data),
  );

  const { data } = useSWR(
    zoneId && settings?.enabled ? ['report:week-visits', zoneId, from, to] : null,
    () => {
      const fromIso = isoDayBounds(from).from;
      const toIso = isoDayBounds(to).to;
      return listZoneVisits(zoneId, { from: fromIso, to: toIso }).then(r => r.data);
    },
  );

  if (!settings?.enabled) return null;
  const visits: VisitSummaryDto[] = (data?.results ?? []).filter(v => !v.is_draft);
  if (visits.length === 0) return null;

  const totals = visits.map(v => v.total).filter((v): v is number => v !== null);
  const avg = totals.length > 0 ? totals.reduce((a, b) => a + b, 0) / totals.length : null;

  return (
    <Card>
      <CardContent className="space-y-2.5 p-3">
        <div className="flex items-center gap-2">
          <span className={cn('size-2.5 shrink-0 rounded-full', DOT)} />
          <span className="text-sm font-semibold text-foreground">{t('visits.reportPanelTitle')}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {avg !== null ? `${Math.round(avg)}%` : '—'}
            </div>
            <div className="text-xs text-muted-foreground">{t('visits.statsColTotal')}</div>
          </div>
          <div>
            <div className="text-2xl font-bold tabular-nums text-foreground">{visits.length}</div>
            <div className="text-xs text-muted-foreground">{t('visits.reportPanelTitle')}</div>
          </div>
        </div>
        <ul className="divide-y divide-border pt-1">
          {visits.map(v => {
            const time = new Date(v.visit_time).toLocaleString(locale, {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            });
            return (
              <li key={v.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/usage/visits/${v.id}`)}
                  className="flex w-full items-center gap-2 py-1.5 text-left text-sm hover:text-primary"
                >
                  <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{v.author.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{time}</span>
                  <TotalScoreBadge total={v.total} />
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
